import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageCircle, Settings, Check, X, ExternalLink, Key, Phone,
  RefreshCw, Send, AlertCircle, Save, ToggleLeft, ToggleRight,
  Edit2, Eye, EyeOff, Copy, CheckCircle, Clock, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import AppHeader from '@/components/shared/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MESSAGE_TYPES = {
  booking_confirmation: {
    title: "Booking Confirmation",
    description: "Sent when a booking is confirmed",
    icon: "📅"
  },
  appointment_reminder: {
    title: "Appointment Reminder",
    description: "Sent 24 hours before appointment",
    icon: "⏰"
  },
  service_completion_feedback: {
    title: "Service Completion & Feedback",
    description: "Sent after service completion with feedback link",
    icon: "✨"
  },
  payment_receipt: {
    title: "Payment Receipt",
    description: "Sent after payment is received",
    icon: "🧾"
  },
  therapist_alert: {
    title: "Therapist Alert",
    description: "Sent to therapist for new bookings",
    icon: "🔔"
  }
};

const WhatsAppConfiguration = ({ user, onLogout }) => {
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState({});
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('setup');
  
  // Setup form
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [businessAccountId, setBusinessAccountId] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [connecting, setConnecting] = useState(false);
  
  // Test message
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Template editing
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editedMessage, setEditedMessage] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [configRes, templatesRes, logsRes] = await Promise.all([
        axios.get(`${API}/whatsapp/config`, { headers }),
        axios.get(`${API}/whatsapp/templates`, { headers }),
        axios.get(`${API}/whatsapp/logs?limit=20`, { headers })
      ]);
      
      setConfig(configRes.data);
      setTemplates(templatesRes.data.templates || {});
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load WhatsApp configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!phoneNumberId || !accessToken) {
      toast.error('Please enter Phone Number ID and Access Token');
      return;
    }
    
    setConnecting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/whatsapp/configure`, {
        phone_number_id: phoneNumberId,
        access_token: accessToken,
        business_account_id: businessAccountId || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('WhatsApp connected successfully!');
      setPhoneNumberId('');
      setAccessToken('');
      setBusinessAccountId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect WhatsApp');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect WhatsApp? Automated messages will stop.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/whatsapp/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('WhatsApp disconnected');
      setConfig({ is_configured: false });
    } catch (error) {
      toast.error('Failed to disconnect WhatsApp');
    }
  };

  const handleToggleTemplate = async (templateKey, currentEnabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/whatsapp/templates/${templateKey}`, {
        template_key: templateKey,
        enabled: !currentEnabled,
        message_template: templates[templateKey]?.message_template || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTemplates(prev => ({
        ...prev,
        [templateKey]: {
          ...prev[templateKey],
          enabled: !currentEnabled
        }
      }));
      
      toast.success(`${MESSAGE_TYPES[templateKey]?.title} ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleEditTemplate = (templateKey) => {
    setEditingTemplate(templateKey);
    setEditedMessage(templates[templateKey]?.message_template || '');
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/whatsapp/templates/${editingTemplate}`, {
        template_key: editingTemplate,
        enabled: templates[editingTemplate]?.enabled ?? true,
        message_template: editedMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTemplates(prev => ({
        ...prev,
        [editingTemplate]: {
          ...prev[editingTemplate],
          message_template: editedMessage
        }
      }));
      
      toast.success('Template saved successfully');
      setEditingTemplate(null);
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSendTest = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whatsapp/test?phone=${testPhone}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.sent) {
        toast.success('Test message sent successfully!');
        fetchData(); // Refresh logs
      } else {
        toast.error(response.data.error || 'Failed to send test message');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader user={user} onLogout={onLogout} showBack={true} backTo="/admin/settings" />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onLogout={onLogout} showBack={true} backTo="/admin/settings" />
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif text-foreground">WhatsApp Configuration</h1>
              <p className="text-muted-foreground">Configure automated WhatsApp messages for your spa</p>
            </div>
          </div>
          
          {config?.is_configured && (
            <Badge variant="default" className="bg-green-600">
              <CheckCircle className="w-3 h-3 mr-1" /> Connected
            </Badge>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="setup" data-testid="whatsapp-setup-tab">
              <Settings className="w-4 h-4 mr-2" /> Setup
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="whatsapp-templates-tab">
              <MessageCircle className="w-4 h-4 mr-2" /> Message Templates
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="whatsapp-logs-tab">
              <Clock className="w-4 h-4 mr-2" /> Message Logs
            </TabsTrigger>
            <TabsTrigger value="faq" data-testid="whatsapp-faq-tab">
              <AlertCircle className="w-4 h-4 mr-2" /> FAQ
            </TabsTrigger>
          </TabsList>

          {/* Setup Tab */}
          <TabsContent value="setup" className="space-y-6">
            {/* Info Card */}
            <Card className="border-green-200 bg-green-50/50">
              <CardHeader>
                <CardTitle className="text-green-800 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Cloud API
                </CardTitle>
                <CardDescription className="text-green-700">
                  Connect your WhatsApp Business account to send automated messages to customers and therapists.
                  <br />
                  <strong>Note:</strong> You pay Meta directly for message costs - no intermediaries!
                </CardDescription>
              </CardHeader>
              <CardContent>
                <a 
                  href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-green-700 hover:text-green-800 underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  How to get WhatsApp API credentials (Meta Business Suite)
                </a>
              </CardContent>
            </Card>

            {config?.is_configured ? (
              /* Connected State */
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    WhatsApp Connected
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Phone Number ID</Label>
                      <p className="font-mono text-sm">{config.phone_number_id}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Connected Since</Label>
                      <p className="text-sm">{formatDate(config.connected_at)}</p>
                    </div>
                  </div>
                  
                  {/* Test Message */}
                  <div className="p-4 bg-muted rounded-lg">
                    <Label className="mb-2 block">Send Test Message</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Phone number with country code (e.g., +919876543210)"
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                      />
                      <Button 
                        onClick={handleSendTest}
                        disabled={sendingTest}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {sendingTest ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Test</>}
                      </Button>
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    onClick={handleDisconnect}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <X className="w-4 h-4 mr-2" /> Disconnect WhatsApp
                  </Button>
                </CardContent>
              </Card>
            ) : (
              /* Setup Form */
              <Card>
                <CardHeader>
                  <CardTitle>Connect WhatsApp Business API</CardTitle>
                  <CardDescription>
                    Enter your credentials from Meta Business Suite
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Phone Number ID <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="e.g., 123456789012345"
                      value={phoneNumberId}
                      onChange={(e) => setPhoneNumberId(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Found in WhatsApp Manager → Phone Numbers
                    </p>
                  </div>
                  
                  <div>
                    <Label>Access Token <span className="text-red-500">*</span></Label>
                    <div className="flex gap-2">
                      <Input
                        type={showToken ? "text" : "password"}
                        placeholder="Permanent access token"
                        value={accessToken}
                        onChange={(e) => setAccessToken(e.target.value)}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setShowToken(!showToken)}
                      >
                        {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Generate a permanent token in Meta Business Suite → System Users
                    </p>
                  </div>
                  
                  <div>
                    <Label>Business Account ID (Optional)</Label>
                    <Input
                      placeholder="e.g., 123456789012345"
                      value={businessAccountId}
                      onChange={(e) => setBusinessAccountId(e.target.value)}
                    />
                  </div>
                  
                  <Button 
                    onClick={handleConnect}
                    disabled={connecting}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {connecting ? (
                      <>Connecting...</>
                    ) : (
                      <><MessageCircle className="w-4 h-4 mr-2" /> Connect WhatsApp</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            {!config?.is_configured && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Connect WhatsApp first to configure message templates.
              </div>
            )}
            
            {Object.entries(MESSAGE_TYPES).map(([key, meta]) => {
              const template = templates[key] || {};
              const isEnabled = template.enabled !== false;
              
              return (
                <Card key={key} className={!isEnabled ? 'opacity-60' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{meta.icon}</span>
                        <div>
                          <CardTitle className="text-base">{meta.title}</CardTitle>
                          <CardDescription className="text-sm">{meta.description}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => handleToggleTemplate(key, isEnabled)}
                          disabled={!config?.is_configured}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditTemplate(key)}
                          disabled={!config?.is_configured}
                        >
                          <Edit2 className="w-3 h-3 mr-1" /> Edit
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="p-3 bg-muted rounded-lg text-sm font-mono whitespace-pre-wrap max-h-32 overflow-y-auto">
                      {template.message_template || 'No template configured'}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Recent Messages</CardTitle>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {logs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No messages sent yet
                  </p>
                ) : (
                  <div className="space-y-2">
                    {logs.map((log) => (
                      <div key={log.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          {log.status === 'sent' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{log.to_phone}</p>
                            <p className="text-xs text-muted-foreground">
                              {MESSAGE_TYPES[log.template_key]?.title || log.template_key}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={log.status === 'sent' ? 'default' : 'destructive'}>
                            {log.status}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(log.sent_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-green-600" />
                  FAQ: Why Configure WhatsApp Business API?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <details className="group border border-slate-200 rounded-lg">
                  <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-foreground hover:bg-slate-50 rounded-lg">
                    <span>What is WhatsApp Business API?</span>
                    <span className="transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="p-4 pt-0 text-muted-foreground text-sm">
                    <p>The <strong>WhatsApp Business API</strong> (also called WhatsApp Cloud API) allows businesses to send automated messages to customers at scale.</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Send booking confirmations instantly</li>
                      <li>Automated appointment reminders</li>
                      <li>Service completion notifications with feedback links</li>
                      <li>Payment receipts</li>
                    </ul>
                    <p className="mt-2">Unlike regular WhatsApp, this is designed for business automation and doesn't require keeping your phone connected.</p>
                  </div>
                </details>

                <details className="group border border-slate-200 rounded-lg">
                  <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-foreground hover:bg-slate-50 rounded-lg">
                    <span>How much does it cost?</span>
                    <span className="transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="p-4 pt-0 text-muted-foreground text-sm">
                    <p>Meta (Facebook) charges per conversation, not per message:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li><strong>Business-initiated:</strong> ~₹0.70 - ₹1.00 per conversation (India)</li>
                      <li><strong>User-initiated:</strong> ~₹0.35 per conversation</li>
                      <li><strong>Free tier:</strong> 1,000 free conversations per month!</li>
                    </ul>
                    <p className="mt-2">A conversation is a 24-hour window where you can send unlimited messages.</p>
                    <p className="mt-2"><a href="https://developers.facebook.com/docs/whatsapp/pricing" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">View WhatsApp Pricing →</a></p>
                  </div>
                </details>

                <details className="group border border-slate-200 rounded-lg">
                  <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-foreground hover:bg-slate-50 rounded-lg">
                    <span>How do I get WhatsApp Business API access?</span>
                    <span className="transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="p-4 pt-0 text-muted-foreground text-sm">
                    <ol className="list-decimal list-inside space-y-2">
                      <li>Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">developers.facebook.com</a></li>
                      <li>Create a Meta Business Account (if you don't have one)</li>
                      <li>Create a new App → Select "Business" type</li>
                      <li>Add "WhatsApp" product to your app</li>
                      <li>Get your <strong>Phone Number ID</strong> and <strong>Access Token</strong></li>
                      <li>Paste them in the Configuration tab above</li>
                    </ol>
                    <p className="mt-3 text-xs bg-amber-50 p-2 rounded">
                      <strong>Tip:</strong> You can use Meta's test phone number for free testing before connecting your own business number.
                    </p>
                  </div>
                </details>

                <details className="group border border-slate-200 rounded-lg">
                  <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-foreground hover:bg-slate-50 rounded-lg">
                    <span>What happens after I connect WhatsApp?</span>
                    <span className="transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="p-4 pt-0 text-muted-foreground text-sm">
                    <p>Once connected, WellNest will automatically:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Send <strong>booking confirmations</strong> when customers book</li>
                      <li>Send <strong>appointment reminders</strong> 24 hours before</li>
                      <li>Send <strong>service completion</strong> messages with feedback links</li>
                      <li>Send <strong>payment receipts</strong> after payment</li>
                    </ul>
                    <p className="mt-2">You can customize all message templates and enable/disable each type individually.</p>
                  </div>
                </details>

                <details className="group border border-slate-200 rounded-lg">
                  <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-foreground hover:bg-slate-50 rounded-lg">
                    <span>Why use WhatsApp instead of SMS?</span>
                    <span className="transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="p-4 pt-0 text-muted-foreground text-sm">
                    <table className="w-full text-sm mt-2">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Feature</th>
                          <th className="text-left py-2">WhatsApp</th>
                          <th className="text-left py-2">SMS</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="py-2">Cost (India)</td>
                          <td className="py-2 text-green-600">~₹0.70/conversation</td>
                          <td className="py-2">₹0.15-0.30/message</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2">Open Rate</td>
                          <td className="py-2 text-green-600">98%</td>
                          <td className="py-2">~20%</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2">Rich Media</td>
                          <td className="py-2 text-green-600">Images, buttons, links</td>
                          <td className="py-2">Text only</td>
                        </tr>
                        <tr>
                          <td className="py-2">Customer Trust</td>
                          <td className="py-2 text-green-600">Verified business badge</td>
                          <td className="py-2">Often marked spam</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </details>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Template Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit {MESSAGE_TYPES[editingTemplate]?.title} Template</DialogTitle>
            <DialogDescription>
              Customize the message sent to customers. Use {'{variable_name}'} for dynamic content.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <strong>Available Variables:</strong>
              <br />
              {'{customer_name}'}, {'{booking_date}'}, {'{booking_time}'}, {'{therapy_type}'}, 
              {'{property_name}'}, {'{total_amount}'}, {'{payment_mode}'}, {'{therapist_name}'}, 
              {'{room_number}'}, {'{feedback_link}'}
            </div>
            
            <Textarea
              value={editedMessage}
              onChange={(e) => setEditedMessage(e.target.value)}
              rows={10}
              className="font-mono text-sm"
              placeholder="Enter your message template..."
            />
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                Cancel
              </Button>
              <Button onClick={handleSaveTemplate} disabled={savingTemplate}>
                {savingTemplate ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Template</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppConfiguration;
